/**
 * SidenavCollapse — A single collapsible item in the sidenav
 */

import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import KDBox from "components/KDBox";
import { useMaterialUIController } from "context";

function SidenavCollapse({ icon, name, active, badge, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, sidenavColor } = controller;

  // whiteSidenav means "Light" sidenav - always use dark text on light background
  const isLight = transparentSidenav || whiteSidenav;

  return (
    <ListItem component="li" sx={{ margin: "2px 0" }}>
      <KDBox
        {...rest}
        sx={(theme) => {
          const { palette, transitions, borders, functions } = theme;
          const { pxToRem } = functions;

          return {
            display: "flex",
            alignItems: "center",
            justifyContent: miniSidenav ? "center" : "flex-start",
            width: "100%",
            padding: miniSidenav
              ? `${pxToRem(8)} ${pxToRem(0)}`
              : `${pxToRem(8)} ${pxToRem(16)}`,
            margin: `0 ${pxToRem(8)}`,
            borderRadius: borders.borderRadius.md,
            cursor: "pointer",
            userSelect: "none",
            whiteSpace: "nowrap",
            transition: transitions.create(["background", "color", "padding", "justify-content"], {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.shorter,
            }),

            backgroundColor: active
              ? isLight
                ? palette.grey[200]
                : palette[sidenavColor]?.main || palette.primary.main
              : "transparent",

            "&:hover": {
              backgroundColor: active
                ? undefined
                : isLight
                  ? palette.grey[100]
                  : "rgba(255,255,255,0.08)",
            },
          };
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 32,
            minHeight: 32,
            display: "grid",
            placeItems: "center",
            color: active
              ? isLight ? "dark" : "#fff"
              : isLight ? "text.primary" : "rgba(255,255,255,0.7)",
          }}
        >
          <Badge
            badgeContent={badge || 0}
            color="error"
            invisible={!badge}
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.65rem",
                height: 16,
                minWidth: 16,
                padding: "0 4px",
              },
            }}
          >
            {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
          </Badge>
        </ListItemIcon>

        <ListItemText
          primary={name}
          sx={(theme) => ({
            marginLeft: miniSidenav ? 0 : theme.functions.pxToRem(4),
            opacity: miniSidenav ? 0 : 1,
            maxWidth: miniSidenav ? 0 : "100%",
            overflow: "hidden",
            transition: theme.transitions.create(["opacity", "margin", "max-width"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.shorter,
            }),
            "& .MuiListItemText-primary": {
              fontWeight: active ? 600 : 400,
              fontSize: "0.875rem",
              color: active
                ? isLight ? "dark" : "#fff"
                : isLight ? "text.primary" : "rgba(255,255,255,0.8)",
            },
          })}
        />
      </KDBox>
    </ListItem>
  );
}

export default SidenavCollapse;
